import type { AppLanguage } from "@/i18n/init";
import { TALK_NOTE_SAMPLE_TEMPLATE } from "./sampleTemplate";

// A "From template" entry. `title`/`description` are i18n keys resolved with
// t(); `content` holds the ready-made markdown body keyed by UI language.
// `en` is required and used as the fallback for any language without its own
// copy (e.g. zh falls back to en).
export interface TalkNoteTemplate {
  id: string;
  title: string;
  description: string;
  content: { en: string } & Partial<Record<AppLanguage, string>>;
}

export function templateContent(
  tpl: TalkNoteTemplate,
  language: AppLanguage,
): string {
  return tpl.content[language] ?? tpl.content.en;
}

const REPORT_EN = `# Title

## Overview
_One-paragraph summary of what this report covers._

## Background & Purpose
-

## Body
### Section 1

### Section 2

## Conclusion
-

## References
-
`;

const REPORT_KO = `# 제목

## 개요
_이 보고서가 다루는 내용을 한 문단으로 요약._

## 배경 · 목적
-

## 본론
### 소주제 1

### 소주제 2

## 결론
-

## 참고자료
-
`;

const WRONG_ANSWER_EN = `# Unit / Topic — YYYY-MM-DD

## Problem
_Paste the question or describe it._

## My attempt
_What I answered or how I approached it._

## Answer & explanation

## Why I got it wrong
- [ ] Concept confusion
- [ ] Calculation slip
- [ ] Misread the question
- [ ] Other

## Related concepts

## Retry
- [ ] Solved again correctly
`;

const WRONG_ANSWER_KO = `# 단원 · 주제 — YYYY-MM-DD

## 문제
_문제 원문을 붙여넣거나 설명._

## 내 풀이
_내가 쓴 답 또는 접근 방법._

## 정답 · 해설

## 틀린 이유
- [ ] 개념 혼동
- [ ] 계산 실수
- [ ] 문제 오독
- [ ] 기타

## 관련 개념

## 다시 풀기
- [ ] 다시 풀어서 맞음
`;

const SUMMARY_EN = `# Topic

## One-line summary

## Key points
-

## Details
### Point 1

## Keywords & terms
-

## To explore further
-
`;

const SUMMARY_KO = `# 주제

## 한 줄 요약

## 핵심 포인트
-

## 상세 정리
### 항목 1

## 키워드 · 용어
-

## 더 볼 것
-
`;

const LECTURE_EN = `# Lecture — YYYY-MM-DD

## Guiding question
_What should this lecture answer?_

## Notes

## Cue column
_Keywords and questions pulled from the notes._

## Summary
_Recap the lecture in three lines._
`;

const LECTURE_KO = `# 강의 — YYYY-MM-DD

## 핵심 질문
_이 수업에서 답할 질문._

## 필기

## 단서 칸
_필기에서 뽑은 키워드와 질문._

## 요약
_수업을 세 줄로 정리._
`;

const CONCEPT_EN = `# Concept

## One-line definition

## Detailed explanation

## Examples
-

## Easy to confuse with
_Similar concepts and how they differ._

## Check questions
-
`;

const CONCEPT_KO = `# 개념

## 한 줄 정의

## 자세한 설명

## 예시
-

## 헷갈리는 점
_비슷한 개념과 차이._

## 확인 질문
-
`;

const STUDY_PLAN_EN = `# Study Plan — Week of YYYY-MM-DD

## Goals for this week
-

## Daily plan

| Day | Subject | Amount | Done |
| --- | --- | --- | --- |
| Mon |  |  | ☐ |
| Tue |  |  | ☐ |
| Wed |  |  | ☐ |
| Thu |  |  | ☐ |
| Fri |  |  | ☐ |
| Sat |  |  | ☐ |
| Sun |  |  | ☐ |

## To review
-

## Reflection
-
`;

const STUDY_PLAN_KO = `# 학습 계획 — YYYY-MM-DD 주차

## 이번 주 목표
-

## 요일별 계획

| 요일 | 과목 | 분량 | 완료 |
| --- | --- | --- | --- |
| 월 |  |  | ☐ |
| 화 |  |  | ☐ |
| 수 |  |  | ☐ |
| 목 |  |  | ☐ |
| 금 |  |  | ☐ |
| 토 |  |  | ☐ |
| 일 |  |  | ☐ |

## 복습할 것
-

## 회고
-
`;

const LAB_EN = `# Investigation Title

## Question / Hypothesis

## Materials & method
-

## Observations & results
_Add tables or data here._

## Conclusion

## Further questions
-
`;

const LAB_KO = `# 탐구 제목

## 탐구 질문 · 가설

## 준비물 · 방법
-

## 관찰 · 결과
_표나 데이터를 여기에 추가._

## 결론

## 더 알아볼 점
-
`;

const MARKDOWN_GUIDE_KO = `# 마크다운 가이드

> TalkNote가 지원하는 마크다운 문법을 한눈에 보여주는 예시 문서.

## 제목
\`#\`, \`##\`, \`###\` 로 제목 단계를 표현.

## 강조
*기울임* 또는 _기울임_, **굵게** 또는 __굵게__, ***굵은 기울임***, ~~취소선~~

위첨자 X<sup>2</sup>, 아래첨자 O<sub>2</sub>

## 목록
- 순서 없는 항목
  - 하위 항목
1. 순서 있는 항목
2. 두 번째

## 할 일
- [x] 완료한 일
- [ ] 남은 일

## 인용
> 질문을 구조화된 이해로 바꾸는 공간.

## 표

| 항목 | 설명 |
| --- | --- |
| 셀 | 내용 |

## 코드
\`인라인 코드\` 또는 아래 블록:

\`\`\`python
def hello():
    print("TalkNote")
\`\`\`

## 수식
인라인 $E = mc^2$, 블록:

$$
\\int_0^1 x^2 \\, dx = \\frac{1}{3}
$$

## 링크
[AI TokTok](https://example.com)
`;

export const TALK_NOTE_TEMPLATES: TalkNoteTemplate[] = [
  {
    id: "report",
    title: "Report",
    description: "Structured report with overview, body, and conclusion.",
    content: { en: REPORT_EN, ko: REPORT_KO },
  },
  {
    id: "wrong-answer-note",
    title: "Wrong-answer note",
    description: "Log a problem you missed and why, for later review.",
    content: { en: WRONG_ANSWER_EN, ko: WRONG_ANSWER_KO },
  },
  {
    id: "summary-note",
    title: "Summary note",
    description: "Condense a topic into key points and takeaways.",
    content: { en: SUMMARY_EN, ko: SUMMARY_KO },
  },
  {
    id: "lecture-note",
    title: "Lecture note",
    description: "Cornell-style notes for classes and video lectures.",
    content: { en: LECTURE_EN, ko: LECTURE_KO },
  },
  {
    id: "concept-note",
    title: "Concept note",
    description: "Explain one concept in depth with examples.",
    content: { en: CONCEPT_EN, ko: CONCEPT_KO },
  },
  {
    id: "study-plan",
    title: "Study plan",
    description: "Weekly planner with goals, schedule, and review.",
    content: { en: STUDY_PLAN_EN, ko: STUDY_PLAN_KO },
  },
  {
    id: "lab-report",
    title: "Lab report",
    description: "Inquiry report with hypothesis, method, and results.",
    content: { en: LAB_EN, ko: LAB_KO },
  },
  {
    id: "markdown-guide",
    title: "Markdown guide",
    description: "Reference draft showing supported Markdown features.",
    content: { en: TALK_NOTE_SAMPLE_TEMPLATE, ko: MARKDOWN_GUIDE_KO },
  },
];
